import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer,
  FormControlLabel, IconButton, Link, MenuItem, Radio, RadioGroup, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, Description as CvIcon, Send as SendIcon, UploadFile as UploadIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { useUserContext } from 'shared/contexts/UserContext';
import { labAvatarSx, labColor, tint } from './labTheme';

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// ── Stats (PI only) ─────────────────────────────────────────────────────────

function Tile({ label, value, sub }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', minWidth: 0 }}>
      <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>{label}</Typography>
      {sub && <Typography variant="caption" color="text.disabled" display="block" noWrap sx={{ fontSize: '0.68rem' }}>{sub}</Typography>}
    </Box>
  );
}

export function LabStatsBody({ lab }) {
  const { data: s } = useSWR(`/consortium/labs/${lab.id}/stats/`, fetcher, { revalidateOnFocus: false });
  const color = labColor(lab);
  if (!s) return <Box display="grid" sx={{ placeItems: 'center', py: 2 }}><CircularProgress size={22} /></Box>;
  const weeks = s.followers_by_week || [];
  const max = Math.max(1, ...weeks.map((w) => w.new));
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        <Tile label="Followers" value={s.followers} sub={s.followers_30d ? `+${s.followers_30d} in 30 days` : 'none new in 30 days'} />
        <Tile label="Page views, 30 days" value={s.views_30d} sub={`${s.viewers_30d} visitor${s.viewers_30d === 1 ? '' : 's'}, members excluded`} />
        <Tile label="Calls suggested" value={s.calls_suggested} />
        <Tile label="Applications" value={s.applications} sub={s.applications_new ? `${s.applications_new} new` : undefined} />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 0.5 }}>New followers per week</Typography>
      <Box role="img" aria-label={`New followers per week: ${weeks.map((w) => w.new).join(', ')}`}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 44, borderBottom: '1px solid', borderColor: 'divider' }}>
        {weeks.map((w) => (
          <Tooltip key={w.week_start} title={`Week of ${fmtDay(w.week_start)}: ${w.new} new · ${w.followers} total`} placement="top">
            <Box sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}>
              <Box sx={{ width: '100%', height: `${Math.max(w.new ? 8 : 2, (w.new / max) * 100)}%`, bgcolor: w.new ? color : 'divider', borderRadius: '4px 4px 0 0' }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

// ── Relevant funding calls ──────────────────────────────────────────────────

export function LabCallsBody({ lab }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const { data: calls, error } = useSWR(`/consortium/labs/${lab.id}/calls/?limit=20`, fetcher, { revalidateOnFocus: false });
  if (error) return <Typography variant="body2" color="text.secondary">Could not load matching calls.</Typography>;
  if (!calls) return <Box display="grid" sx={{ placeItems: 'center', py: 2 }}><CircularProgress size={22} /></Box>;
  if (!calls.length) {
    return <Typography variant="body2" color="text.secondary">No open calls match the lab yet. Keywords and a description help.</Typography>;
  }
  const shown = all ? calls : calls.slice(0, 5);
  return (
    <Box>
      {shown.map((c) => (
        <Box key={c.id} onClick={() => navigate(`/funding/call/${c.id}`)}
          sx={{ py: 0.9, cursor: 'pointer', borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 'none' },
            '&:hover .title': { textDecoration: 'underline' } }}>
          <Typography className="title" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{c.title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {[c.program, c.closes_at && `closes ${fmtDay(c.closes_at)}`].filter(Boolean).join(' · ')}
          </Typography>
          {c.matched?.length > 0 && (
            <Typography variant="caption" color="text.disabled" display="block" noWrap>Matches {c.matched.join(', ')}</Typography>
          )}
        </Box>
      ))}
      {calls.length > 5 && (
        <Button size="small" onClick={() => setAll(!all)} sx={{ mt: 0.5, px: 0 }}>
          {all ? 'Show fewer' : `See more (${calls.length - 5})`}
        </Button>
      )}
    </Box>
  );
}

// ── Followers (members only) ────────────────────────────────────────────────

export function LabFollowersBody({ lab }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const { data: rows } = useSWR(`/consortium/labs/${lab.id}/followers/`, fetcher, { revalidateOnFocus: false });
  if (!rows) return <Box display="grid" sx={{ placeItems: 'center', py: 2 }}><CircularProgress size={22} /></Box>;
  if (!rows.length) return <Typography variant="body2" color="text.secondary">No followers yet.</Typography>;
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <Box>
      {shown.map((f) => (
        <Box key={f.id} display="flex" alignItems="center" gap={1} py={0.5} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${f.id}`)}>
          <Avatar src={f.avatar_url || undefined} sx={{ width: 28, height: 28, fontSize: '0.8rem', ...labAvatarSx(lab) }}>{(f.name || '?')[0]}</Avatar>
          <Box minWidth={0} flex={1}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{f.name}</Typography>
            {f.headline && <Typography variant="caption" color="text.secondary" display="block" noWrap>{f.headline}</Typography>}
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(new Date(f.followed_at), { addSuffix: true })}
          </Typography>
        </Box>
      ))}
      {rows.length > 8 && <Button size="small" onClick={() => setAll(!all)} sx={{ px: 0 }}>{all ? 'Show fewer' : `All ${rows.length} followers`}</Button>}
    </Box>
  );
}

// ── Apply for a position ────────────────────────────────────────────────────

// Used for a lab's positions (LAB given) and for any opening in the Hub (APPLY_URL, HOST_NAME, RECIPIENT given)
export function ApplyDialog({ open, onClose, lab, position, onApplied, applyUrl, hostName, recipient, profileCv }) {
  const { showSnackbar } = useSnackbar();
  const hasProfileCv = Boolean(profileCv ?? lab?.viewer_has_profile_cv);
  const [note, setNote] = useState('');
  const [cvMode, setCvMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setNote(''); setFile(null); setError(null); }
  }, [open]);
  useEffect(() => { setCvMode(hasProfileCv ? 'profile' : 'upload'); }, [hasProfileCv]);

  const send = async () => {
    setBusy(true); setError(null);
    const fd = new FormData();
    fd.append('note', note);
    if (cvMode === 'upload' && file) fd.append('cv', file);
    if (cvMode === 'profile') fd.append('use_profile_cv', 'true');
    try {
      const res = await axiosService.post(applyUrl || `/consortium/labs/${lab.id}/positions/${position.id}/apply/`, fd);
      onApplied(res.data);
      showSnackbar(`Application sent to ${hostName || lab?.name}`, 'success');
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the application.'));
    } finally {
      setBusy(false);
    }
  };

  if (!position) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply: {position.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {recipient || `${lab?.pi_detail?.name}, the lab's PI,`} receives your application together with your Otable profile.
        </Typography>
        <TextField label="Cover note" multiline minRows={5} fullWidth value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Why this position, your background, when you could start…" />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>CV</Typography>
        <RadioGroup value={cvMode} onChange={(e) => setCvMode(e.target.value)}>
          {hasProfileCv && <FormControlLabel value="profile" control={<Radio size="small" />} label="Attach the CV on my profile" />}
          <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload a PDF" />
          <FormControlLabel value="none" control={<Radio size="small" />} label="No CV; my Otable profile is enough" />
        </RadioGroup>
        {cvMode === 'upload' && (
          <Box display="flex" alignItems="center" gap={1}>
            <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>
              {file ? 'Change file' : 'Choose PDF'}
            </Button>
            {file && <Typography variant="caption" color="text.secondary" noWrap>{file.name}</Typography>}
          </Box>
        )}
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={send} disabled={busy || !note.trim() || (cvMode === 'upload' && !file)}>Send application</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Applicants (editors) ────────────────────────────────────────────────────

const STATUS_LABEL = { new: 'New', shortlisted: 'Shortlisted', declined: 'Declined' };

export function ApplicantsDialog({ open, onClose, lab, position, base }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  // BASE: /consortium/positions/<id>/applications for a Hub opening; the lab's applications otherwise
  const apps = base || (lab ? `/consortium/labs/${lab.id}/applications` : '');
  const key = open && position ? (base ? `${base}/` : `${apps}/?position=${position.id}`) : null;
  const { data: rows, mutate } = useSWR(key, fetcher);

  const setStatus = async (app, status) => {
    try {
      const res = await axiosService.patch(`${apps}/${app.id}/`, { status });
      mutate(rows.map((r) => (r.id === app.id ? res.data : r)), { revalidate: false });
    } catch (err) {
      showSnackbar(apiErrorMessage(err), 'error');
    }
  };
  const openCv = async (app) => {
    try {
      const res = await axiosService.get(`${apps}/${app.id}/cv/`, { responseType: 'blob' });
      // A download link, not a pop-up: pop-ups opened after a request are blocked by browsers
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CV_${(app.applicant_detail?.name || 'applicant').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      showSnackbar('Could not open the CV', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Applicants: {position?.title}</DialogTitle>
      <DialogContent dividers>
        {!rows ? <Box display="grid" sx={{ placeItems: 'center', py: 4 }}><CircularProgress /></Box>
          : rows.length === 0 ? <Typography color="text.secondary">No applications yet.</Typography>
          : rows.map((a) => (
            <Box key={a.id} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar src={a.applicant_detail?.avatar_url || undefined} sx={{ width: 40, height: 40, ...(lab ? labAvatarSx(lab) : {}) }}>
                  {(a.applicant_detail?.name || '?')[0]}
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Link component="button" variant="body1" underline="hover" sx={{ fontWeight: 600 }}
                    onClick={() => { onClose(); navigate(`/profile/${a.applicant}`); }}>
                    {a.applicant_detail?.name}
                  </Link>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {[a.applicant_detail?.headline, a.applicant_detail?.email, `applied ${formatDistanceToNow(new Date(a.created), { addSuffix: true })}`].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {a.has_cv && <Button size="small" startIcon={<CvIcon />} onClick={() => openCv(a)}>CV</Button>}
                <TextField select size="small" value={a.status} onChange={(e) => setStatus(a, e.target.value)} sx={{ minWidth: 130 }}>
                  {Object.entries(STATUS_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                </TextField>
              </Box>
              {a.note && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line', pl: 6.5 }}>{a.note}</Typography>}
            </Box>
          ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Team chat (members) ─────────────────────────────────────────────────────

export function LabChatDrawer({ open, onClose, lab }) {
  const { user } = useUserContext();
  const { showSnackbar } = useSnackbar();
  const { data: rows, mutate } = useSWR(open ? `/consortium/labs/${lab.id}/messages/` : null, fetcher,
    { refreshInterval: open ? 15000 : 0 });
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const color = labColor(lab);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [rows?.length, open]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await axiosService.post(`/consortium/labs/${lab.id}/messages/`, { body: text });
      mutate([...(rows || []), res.data], { revalidate: false });
      setBody('');
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'Could not send the message.'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, top: 64, height: 'calc(100% - 64px)', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', bgcolor: color, color: '#fff' }}>
        <Box flex={1}>
          <Typography sx={{ fontWeight: 700 }}>{lab.name}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>Team chat · {lab.members.length} members</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close chat"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, bgcolor: tint(color, 0.04) }}>
        {!rows ? <Box display="grid" sx={{ placeItems: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          : rows.length === 0 ? <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>No messages yet. Say hello to the lab.</Typography>
          : rows.map((m) => {
            const mine = String(m.author) === String(user?.id);
            return (
              <Box key={m.id} display="flex" gap={1} mb={1.25} flexDirection={mine ? 'row-reverse' : 'row'}>
                <Tooltip title={m.author_detail?.name || ''}>
                  <Avatar src={m.author_detail?.avatar_url || undefined}
                    sx={{ width: 32, height: 32, fontSize: '0.85rem', mt: mine ? 0 : 2.25, flexShrink: 0, ...labAvatarSx(lab) }}>
                    {(m.author_detail?.name || '?')[0]}
                  </Avatar>
                </Tooltip>
                <Box sx={{ maxWidth: '78%' }}>
                  {!mine && <Typography variant="caption" color="text.secondary">{m.author_detail?.name}</Typography>}
                  <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: mine ? color : 'background.paper', color: mine ? '#fff' : 'text.primary',
                    boxShadow: mine ? 'none' : '0 1px 2px rgba(0,0,0,0.08)' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.disabled" display="block" textAlign={mine ? 'right' : 'left'} sx={{ fontSize: '0.68rem' }}>
                    {formatDistanceToNow(new Date(m.created), { addSuffix: true })}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        <div ref={bottomRef} />
      </Box>
      <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField fullWidth size="small" multiline maxRows={5} placeholder="Message the lab…" value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <IconButton onClick={send} disabled={sending || !body.trim()} sx={{ color }} aria-label="Send"><SendIcon /></IconButton>
      </Stack>
    </Drawer>
  );
}

